import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, CircularProgress, Alert, Stack,
  Card, CardContent, Grid, Tooltip, ToggleButton, ToggleButtonGroup,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { adminGetMonthlyComparison } from '../../services/commissionService';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

// Rút gọn cho mobile: "1,23 tỷ" / "63 tr" / "1,6 tr"
// Dưới 10 triệu vẫn giữ 1 số lẻ, nếu không 1.574.300 ₫ bị làm tròn thành "2 tr" — sai lệch quá lớn.
const formatMoneyShort = (a) => {
  const n = Math.abs(a || 0);
  if (n >= 1e9) return `${(a / 1e9).toFixed(2).replace('.', ',')} tỷ`;
  if (n >= 1e7) return `${Math.round(a / 1e6)} tr`;
  if (n >= 1e6) return `${(a / 1e6).toFixed(1).replace('.', ',')} tr`;
  return formatMoney(a);
};

const formatPercent = (p) => (p == null ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(1).replace('.', ',')}%`);

// 'YYYY-MM-DD' -> 'DD/MM'. Cắt chuỗi thuần, KHÔNG new Date() để không dính timezone trình duyệt.
const ddMM = (iso) => {
  const [, m, d] = String(iso || '').split('-');
  return d && m ? `${d}/${m}` : '';
};

const UP = '#059669';
const DOWN = '#dc2626';

/**
 * Ô chênh lệch: mũi tên + % (hoặc "mới" khi kỳ trước = 0 nên không tính được %).
 * incomparable = hai kỳ không cùng độ dài (tháng đang diễn ra so với tháng đủ) → hiện xám kèm
 * cảnh báo thay vì mũi tên đỏ/xanh, tránh đọc nhầm "sụt 95%".
 */
const DeltaCell = ({ amount, percent, size = 'caption', incomparable = false }) => {
  if (amount == null) {
    return <Typography variant={size} color="text.secondary">—</Typography>;
  }
  if (incomparable) {
    return (
      <Tooltip title="Tháng chưa kết thúc — không so được với tháng trước đã đủ ngày. Dùng chế độ “Cùng thời điểm” để so công bằng.">
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
          <Typography variant={size} sx={{ fontWeight: 600, color: '#94a3b8' }}>
            {formatPercent(percent)}
          </Typography>
        </Box>
      </Tooltip>
    );
  }
  if (percent == null) {
    return (
      <Tooltip title={`${amount >= 0 ? '+' : ''}${formatMoney(amount)} — kỳ trước không có doanh thu nên không tính được %`}>
        <Chip
          size="small"
          icon={<FiberNewIcon sx={{ fontSize: 14 }} />}
          label="mới"
          sx={{ bgcolor: '#ecfdf5', color: UP, fontWeight: 700, height: 22 }}
        />
      </Tooltip>
    );
  }
  const flat = percent === 0;
  const up = percent > 0;
  const color = flat ? '#64748b' : up ? UP : DOWN;
  const Icon = flat ? TrendingFlatIcon : up ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Tooltip title={`${amount >= 0 ? '+' : ''}${formatMoney(amount)}`}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Icon sx={{ fontSize: 15, color }} />
        <Typography variant={size} sx={{ fontWeight: 700, color }}>
          {formatPercent(percent)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/** Thẻ lớn: số của tháng này + chênh lệch so với cùng kỳ tháng trước. */
const HeroCard = ({ title, value, prevLabel, prevValue, amount, percent, palette, incomparable }) => (
  <Card elevation={0} sx={{ borderRadius: 2, border: `1px solid ${palette.border}`, bgcolor: palette.bg, height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Typography variant="caption" sx={{ color: palette.label, fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ color: palette.value, my: 0.5 }}>
        {value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <DeltaCell amount={amount} percent={percent} size="body2" incomparable={incomparable} />
        <Typography variant="caption" color="text.secondary">
          {incomparable ? 'so với tháng trước (chưa hết tháng)' : 'so với cùng kỳ tháng trước'}
        </Typography>
      </Box>
      {prevValue != null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {prevLabel}: {formatMoney(prevValue)}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const RevenueMonthlyComparison = ({ isMobile = false, onLoaded }) => {
  const [mode, setMode] = useState('mtd');
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminGetMonthlyComparison({ months, mode });
      if (result.success) {
        setData(result.data);
        // Chỉ đẩy số của chế độ "cùng thời điểm" lên thẻ tổng quan. Số trọn tháng của tháng đang
        // diễn ra là 3/31 ngày so với cả tháng trước → ra -95% vô nghĩa, không được để lọt lên thẻ.
        if (onLoaded && mode === 'mtd') onLoaded(result.data);
      } else {
        setError(result.error?.message || 'Không thể tải dữ liệu so sánh');
      }
    } catch (err) {
      setError('Lỗi kết nối server');
    }
    setLoading(false);
    // onLoaded cố tình không nằm trong deps: nếu cha truyền hàm inline sẽ gây fetch vô hạn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, mode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = data?.rows || [];
  const hero = data?.hero;
  // Ở "trọn tháng", tháng đang diễn ra chưa đủ ngày nên mọi so sánh với tháng trước đều lệch
  const heroIncomparable = mode === 'full' && !!rows[0]?.isPartial;
  // Ngày thực tế có dữ liệu (ở mode full thì cutDay = ngày cuối tháng nên không dùng được)
  const asOfDay = data?.asOf ? Number(data.asOf.split('-')[2]) : null;
  const isEmpty = !loading && rows.length > 0 && rows.every(r => r.totalGMV === 0);
  const colSpan = isMobile ? 4 : 8;   // mobile ẩn 4 cột: Kỳ so sánh, +/− hoa hồng, % HH TB, Trạng thái

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <CompareArrowsIcon sx={{ fontSize: 32, color: '#7c3aed' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>So sánh doanh thu &amp; hoa hồng theo tháng</Typography>
            <Typography variant="body2" color="text.secondary">
              {hero?.windowLabel
                ? (mode === 'mtd' ? hero.windowLabel : `Trọn tháng · ${months} tháng gần nhất`)
                : 'Đang tải…'}
            </Typography>
          </Box>
        </Box>

        {/* Controls */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(e, v) => { if (v) setMode(v); }}
              disabled={loading}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 2, px: 2 } }}
            >
              <ToggleButton value="mtd">Cùng thời điểm</ToggleButton>
              <ToggleButton value="full">Trọn tháng</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="mc-months-label">Số tháng</InputLabel>
              <Select
                labelId="mc-months-label"
                label="Số tháng"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value={3}>3 tháng</MenuItem>
                <MenuItem value={6}>6 tháng</MenuItem>
                <MenuItem value={12}>12 tháng</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Làm mới
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, mb: 3 }}
            action={<Button size="small" onClick={fetchData}>Thử lại</Button>}
          >
            {error}
          </Alert>
        )}

        {/* Hero: tháng này vs tháng trước */}
        {hero && !error && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <HeroCard
                title={`Doanh thu ${hero.current.label}`}
                value={formatMoney(hero.current.totalGMV)}
                prevLabel={hero.previous?.label}
                prevValue={hero.previous?.totalGMV}
                amount={hero.gmvChangeAmount}
                percent={hero.gmvChangePercent}
                incomparable={heroIncomparable}
                palette={{ bg: '#eff6ff', border: '#bfdbfe', label: '#3b82f6', value: '#1e40af' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <HeroCard
                title={`Hoa hồng ${hero.current.label}`}
                value={formatMoney(hero.current.totalCommission)}
                prevLabel={hero.previous?.label}
                prevValue={hero.previous?.totalCommission}
                amount={hero.commissionChangeAmount}
                percent={hero.commissionChangePercent}
                incomparable={heroIncomparable}
                palette={{ bg: '#ecfdf5', border: '#a7f3d0', label: '#10b981', value: '#065f46' }}
              />
            </Grid>
          </Grid>
        )}

        {isEmpty && (
          <Alert severity="info" sx={{ borderRadius: 2, mb: 3 }}>
            Chưa có doanh thu đã duyệt trong {months} tháng gần đây.
          </Alert>
        )}

        {/* Bảng chi tiết */}
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Tháng</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Kỳ so sánh</TableCell>}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Doanh thu</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>+/−</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Hoa hồng</TableCell>
                {!isMobile && <TableCell align="right" sx={{ fontWeight: 700 }}>+/−</TableCell>}
                {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>% HH TB</TableCell>}
                {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Trạng thái</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">Không có dữ liệu</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const incomparable = mode === 'full' && row.isPartial;
                  return (
                  <TableRow key={row.monthKey} hover sx={row.isCurrentMonth ? { bgcolor: '#faf5ff' } : undefined}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700}>{row.label}</Typography>
                      {row.isCurrentMonth && (
                        <Typography variant="caption" color="text.secondary">đang diễn ra</Typography>
                      )}
                    </TableCell>

                    {!isMobile && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {ddMM(row.rangeStart)} – {ddMM(row.rangeEnd)}
                        </Typography>
                      </TableCell>
                    )}

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1e40af' }}>
                        {isMobile ? formatMoneyShort(row.totalGMV) : formatMoney(row.totalGMV)}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" color="text.secondary">
                          {row.revenueCount} phiếu · {row.activeUserCount} NV
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <DeltaCell amount={row.gmvChangeAmount} percent={row.gmvChangePercent} incomparable={incomparable} />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#065f46' }}>
                        {isMobile ? formatMoneyShort(row.totalCommission) : formatMoney(row.totalCommission)}
                      </Typography>
                      {isMobile && (
                        <DeltaCell amount={row.commissionChangeAmount} percent={row.commissionChangePercent} incomparable={incomparable} />
                      )}
                    </TableCell>

                    {!isMobile && (
                      <TableCell align="right">
                        <DeltaCell amount={row.commissionChangeAmount} percent={row.commissionChangePercent} incomparable={incomparable} />
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={`${row.effectiveRate}%`}
                          sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700 }}
                        />
                      </TableCell>
                    )}

                    {!isMobile && (
                      <TableCell align="center">
                        {row.isSnapshot ? (
                          <Chip size="small" label="Đã chốt" color="success" variant="outlined" />
                        ) : incomparable ? (
                          <Tooltip title={`Mới có dữ liệu ${asOfDay}/${row.daysInMonth} ngày — chưa hết tháng nên không so được với tháng đã đủ ngày`}>
                            <Chip size="small" label="Chưa hết tháng" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={row.snapshotAvailable
                            ? 'Tháng đã chốt, nhưng số cùng thời điểm phải tính lại từ phiếu doanh thu nên có thể lệch với bản chốt'
                            : 'Tính trực tiếp từ phiếu doanh thu đã duyệt'}>
                            <Chip
                              size="small"
                              label={row.snapshotAvailable ? 'Tính lại' : 'Tạm tính'}
                              sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Chú thích */}
        {data && !error && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {mode === 'mtd'
              ? `Mỗi tháng chỉ tính tới ngày ${data.cutDay} để so cùng thời điểm. Số liệu tính lại từ phiếu doanh thu đã duyệt.`
              : 'Trọn tháng: tháng đã chốt lấy theo bản chốt (khớp tab Hoa hồng), tháng chưa chốt tính từ phiếu doanh thu đã duyệt.'}
            {' '}Chưa gồm thưởng leader.
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default RevenueMonthlyComparison;
